// src/Components/Header.js
import React, { useState, useRef, useEffect } from 'react';
import { ChevronRight, User, Calendar, Crown, Sparkles, Shield, LogOut } from 'lucide-react';
import { db } from '../firebase/firestore';
import { doc, onSnapshot } from 'firebase/firestore';
import SubscriptionPopup from './SubscriptionPopup';
import { Link } from 'react-router-dom';
import { useUser } from '../context/userContext';
import { useWebAuth } from '../context/WebAuthContext';

const Header = () => {
  const [showSubscriptionPopup, setShowSubscriptionPopup] = useState(false);
  const [showUserInfoPopup, setShowUserInfoPopup] = useState(false);
  const [subscriptionInfo, setSubscriptionInfo] = useState({
    active: false,
    plan: 'PRO',
    price: 55,
    currency: '€',
    period: 'в месяц',
    features: [
      'Полный доступ ко всем урокам',
      'Персональная обратная связь',
      'Доступ к закрытому сообществу',
      'Скачивание материалов'
    ]
  });
  const [loading, setLoading] = useState(true);
  const [userData, setUserData] = useState(null);
  const [avatarUrl, setAvatarUrl] = useState(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const userPopupRef = useRef(null);

  // ✅ ИСПОЛЬЗУЕМ КОНТЕКСТ ДЛЯ PREMIUM СТАТУСА
  const { premium, subscriptionData, id: userId, userType, syncWithFirebase } = useUser();
  
  // ✅ ДЛЯ ВЕБ-ПОЛЬЗОВАТЕЛЕЙ
  const { webUser, logoutUser, isWebUser: checkIsWebUser } = useWebAuth();
  const isWebUser = checkIsWebUser();

  // Определяем, откуда пользователь
  const telegramWebApp = window.Telegram?.WebApp;
  const telegramUser = telegramWebApp?.initDataUnsafe?.user;
  
  // Данные для отображения (Telegram или Web)
  const displayName = isWebUser 
    ? (webUser?.fullName || webUser?.email?.split('@')[0] || 'Гость')
    : (telegramUser?.first_name || 'Гость');
  
  const username = isWebUser 
    ? webUser?.email 
    : telegramUser?.username;
  
  const languageCode = isWebUser 
    ? 'ru' 
    : (telegramUser?.language_code || 'ru');

  // Список администраторов (ID из Telegram и email из Web)
  const ADMIN_IDS = ['272401691', '372145623'];
  const ADMIN_EMAILS = ['admin@inner-art.com', 'your-email@example.com']; // Добавьте email администраторов

  // ✅ Проверяем, является ли пользователь администратором
  useEffect(() => {
    if (isWebUser && webUser?.email) {
      setIsAdmin(ADMIN_EMAILS.includes(webUser.email));
    } else if (userId && ADMIN_IDS.includes(userId.toString())) {
      setIsAdmin(true);
    } else {
      setIsAdmin(false);
    }
  }, [userId, isWebUser, webUser]);

  // ✅ Используем premium из контекста
  useEffect(() => {
    setSubscriptionInfo(prev => ({
      ...prev,
      active: premium
    }));
  }, [premium]);

  // ✅ Real-time подписка на данные пользователя
  useEffect(() => {
    if (!userId || !userType) {
      setLoading(false);
      return;
    }

    const collectionName = userType === 'telegram' ? 'telegramUsers' : 'webUsers';
    const userDocRef = doc(db, collectionName, userId.toString());
    
    const unsubscribe = onSnapshot(userDocRef, (docSnap) => {
      if (docSnap.exists()) {
        const data = docSnap.data();
        setUserData(data);
        
        // Используем subscription.active из Firebase
        const isPremiumActive = data.subscription?.active === true;
        
        setSubscriptionInfo(prev => ({
          ...prev,
          active: isPremiumActive,
          stripeCustomerId: data.subscription?.stripeCustomerId || null
        }));
        
        console.log("📱 Header - User data updated:", {
          userType,
          isPremium: isPremiumActive,
          subscription: data.subscription,
          contextPremium: premium
        });
      } else {
        console.log("📱 Header - No user data found");
        setSubscriptionInfo(prev => ({ ...prev, active: false }));
      }
      
      setLoading(false);
    }, (error) => {
      console.error("❌ Header - Error listening to user data:", error);
      setLoading(false);
    });

    return () => unsubscribe();
  }, [userId, userType, premium]);

  // Загрузка аватарки
  const loadAvatar = (url) => {
    return new Promise((resolve) => {
      if (!url) {
        resolve(null);
        return;
      }
      
      const img = new Image();
      img.onload = () => {
        resolve(url);
      };
      img.onerror = () => {
        console.warn('⚠️ Не удалось загрузить аватар:', url);
        resolve(null);
      };
      img.src = url;
    });
  };

  // Загружаем аватарку
  useEffect(() => {
    const initAvatar = async () => {
      if (isWebUser) {
        // Для веб-пользователей используем gravatar или дефолтную аватарку
        const emailHash = webUser?.email ? btoa(webUser.email.toLowerCase().trim()) : '';
        const gravatarUrl = `https://www.gravatar.com/avatar/${emailHash}?d=identicon&s=200`;
        setAvatarUrl(gravatarUrl);
        return;
      }
      
      const photoUrl = telegramUser?.photo_url;
      
      if (photoUrl) {
        try {
          const loadedUrl = await loadAvatar(photoUrl);
          if (loadedUrl) {
            setAvatarUrl(loadedUrl);
            return;
          }
        } catch (error) {
          console.warn('⚠️ Ошибка при загрузке аватарки:', error);
        }
      }
      
      setAvatarUrl('/2nd.png');
    };

    initAvatar();
  }, [isWebUser, webUser, telegramUser]);

  // Обработка кликов вне попапов
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (userPopupRef.current && !userPopupRef.current.contains(e.target) && showUserInfoPopup) {
        setShowUserInfoPopup(false);
      }
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showUserInfoPopup]);

  // Определяем, какую кнопку показываем
  const getButtonImage = () => {
    if (premium) {
      return '/mingcute.svg';
    }
    return '/club-button.svg';
  };

  // Функция для форматирования даты окончания подписки
  const formatSubscriptionEndDate = () => {
    if (subscriptionData?.currentPeriodEnd) {
      return new Date(subscriptionData.currentPeriodEnd).toLocaleDateString('ru-RU', {
        day: 'numeric',
        month: 'long',
        year: 'numeric'
      });
    }
    return null;
  };

  // Функция для получения названия тарифа
  const getPlanName = () => {
    if (subscriptionData?.planName) {
      return subscriptionData.planName;
    }
    if (subscriptionData?.planId) {
      const plans = {
        '1_month': '1 месяц',
        '3_months': '3 месяца',
        '6_months': '6 месяцев'
      };
      return plans[subscriptionData.planId] || 'PRO';
    }
    return 'PRO';
  };

  // Функция для получения стоимости тарифа
  const getPlanPrice = () => {
    if (subscriptionData?.price) {
      return `${subscriptionData.price} €`;
    }
    return '55 €';
  };

  // Обработчик выхода для веб-пользователей
  const handleLogout = async () => {
    await logoutUser();
    setShowUserInfoPopup(false);
    // Перезагружаем страницу для обновления состояния
    window.location.href = '/';
  };

  // Если данные еще загружаются
  if (loading) {
    return (
      <header>
        <div className="flex items-center justify-between p-4">
          <div className="flex items-center space-x-3 flex-1">
            <div className="relative">
              <img
                src="/2nd.png"
                alt="avatar"
                className="w-10 h-10 rounded-full object-cover border-2 border-purple-500"
              />
              <div className="absolute bottom-0 right-0 w-2.5 h-2.5 bg-green-500 rounded-full border-2 border-white"></div>
            </div>
            <div className="flex items-center gap-1">
              <h2 className="text-white text-base">{displayName}</h2>
              <ChevronRight className="w-4 h-4 text-[#34D3FF]" />
            </div>
          </div>
          <button className="ml-2 opacity-50 animate-pulse">
            <div className="w-28 h-10 bg-gradient-to-r from-purple-500 to-pink-500 rounded-full"></div>
          </button>
        </div>
      </header>
    );
  }

  return (
    <>
      <header>
        <div className="flex items-center justify-between p-4">
          {/* Левая часть: Аватар и имя - кликабельно */}
          <button 
            onClick={() => setShowUserInfoPopup(true)}
            className="flex items-center space-x-3 flex-1 active:opacity-80"
          >
            <div className="relative">
              <img
                src={avatarUrl || '/2nd.png'}
                alt="avatar"
                className="w-10 h-10 rounded-full object-cover border-2 border-purple-500"
                onError={(e) => {
                  e.target.onerror = null;
                  e.target.src = '/2nd.png';
                }}
              />
              <div className="absolute bottom-0 right-0 w-2.5 h-2.5 bg-green-500 rounded-full border-2 border-white"></div>
              {isAdmin && (
                <div className="absolute -top-1 -right-1 w-5 h-5 bg-gradient-to-r from-red-500 to-orange-500 rounded-full flex items-center justify-center">
                  <Shield className="w-3 h-3 text-white" />
                </div>
              )}
            </div>
            <div className="flex items-center gap-1">
              <h2 className="text-white text-base">{displayName}</h2>
              {premium && (
                <Sparkles className="w-4 h-4 text-yellow-400 ml-1" />
              )}
              <ChevronRight className="w-4 h-4 text-[#34D3FF]" />
            </div>
          </button>

          {/* Правая часть: Кнопка подписки */}
          <button 
            onClick={() => setShowSubscriptionPopup(true)}
            className="ml-2 active:scale-95 transition-transform"
          >
            <img 
              src={getButtonImage()} 
              alt={premium ? "Моя подписка" : "Подключить PRO"} 
              className={premium ? "w-auto h-12" : "w-auto h-10"}
            />
          </button>
        </div>
      </header>

      {/* Попап с информацией о пользователе */}
      {showUserInfoPopup && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
          <div 
            ref={userPopupRef}
            className="bg-white rounded-2xl w-full max-w-md overflow-hidden shadow-2xl max-h-[90vh] flex flex-col"
          >
            {/* Заголовок попапа */}
            <div className="bg-gradient-to-r from-[#6C6FF8] to-[#F466C8] p-6 text-white flex-shrink-0">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center">
                  <User className="w-6 h-6 mr-3" />
                  <h2 className="text-xl font-bold">Информация о профиле</h2>
                </div>
                <button 
                  onClick={() => setShowUserInfoPopup(false)}
                  className="p-1 rounded-full bg-white/20 active:bg-white/30"
                >
                  <ChevronRight className="w-5 h-5 rotate-180" />
                </button>
              </div>
            </div>

            {/* Контент попапа */}
            <div className="p-6 overflow-y-auto flex-grow">
              {/* Аватар и основная информация */}
              <div className="flex items-center mb-6">
                <div className="relative mr-4">
                  <img
                    src={avatarUrl || '/2nd.png'}
                    alt="avatar"
                    className="w-16 h-16 rounded-full object-cover border-4 border-purple-500"
                    onError={(e) => {
                      e.target.onerror = null;
                      e.target.src = '/2nd.png';
                    }}
                  />
                  {premium && (
                    <div className="absolute -top-1 -right-1 bg-gradient-to-r from-yellow-400 to-orange-500 w-6 h-6 rounded-full flex items-center justify-center">
                      <Crown className="w-3 h-3 text-white" />
                    </div>
                  )}
                  {isAdmin && (
                    <div className="absolute -bottom-1 -right-1 bg-gradient-to-r from-red-500 to-orange-500 w-6 h-6 rounded-full flex items-center justify-center">
                      <Shield className="w-3 h-3 text-white" />
                    </div>
                  )}
                </div>
                <div>
                  <h3 className="text-xl font-bold text-gray-800">{displayName}</h3>
                  {username && (
                    <p className="text-gray-600 text-sm break-all">
                      {isWebUser ? username : `@${username}`}
                    </p>
                  )}
                  {isWebUser && webUser?.email && (
                    <p className="text-gray-500 text-xs mt-1">{webUser.email}</p>
                  )}
                  <div className="flex flex-wrap items-center gap-2 mt-2">
                    {isAdmin && (
                      <span className="px-2 py-0.5 bg-gradient-to-r from-red-100 to-orange-100 text-red-700 text-xs font-medium rounded-full flex items-center gap-1">
                        <Shield className="w-3 h-3" />
                        Администратор
                      </span>
                    )}
                    {premium && (
                      <span className="px-2 py-0.5 bg-gradient-to-r from-yellow-100 to-orange-100 text-yellow-700 text-xs font-medium rounded-full flex items-center gap-1">
                        <Crown className="w-3 h-3" />
                        PRO подписка
                      </span>
                    )}
                    {isWebUser && (
                      <span className="px-2 py-0.5 bg-blue-100 text-blue-700 text-xs font-medium rounded-full flex items-center gap-1">
                        <User className="w-3 h-3" />
                        Веб-пользователь
                      </span>
                    )}
                  </div>
                </div>
              </div>

              {/* Детали профиля */}
              <div className="space-y-4 mb-6">
                <div className={`bg-gradient-to-r ${premium ? 'from-purple-50 to-pink-50 border-purple-100' : 'from-gray-50 to-gray-50 border-gray-200'} border rounded-lg p-4`}>
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center">
                      <Crown className={`w-5 h-5 mr-2 ${premium ? 'text-yellow-500' : 'text-gray-400'}`} />
                      <span className="font-semibold text-gray-800">
                        {premium ? 'PRO подписка' : 'Базовый доступ'}
                      </span>
                    </div>
                    <span className={`px-3 py-1 rounded-full text-xs font-medium ${premium ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'}`}>
                      {premium ? 'Активна' : 'Не активна'}
                    </span>
                  </div>
                  
                  {premium && subscriptionData ? (
                    <div className="text-sm text-gray-600 space-y-2">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-medium">Тариф:</span>
                        <span className="bg-purple-100 text-purple-800 px-2 py-0.5 rounded-full text-xs">
                          {getPlanName()}
                        </span>
                      </div>
                      
                      {subscriptionData.price && (
                        <p>💰 Стоимость: <span className="font-medium">{getPlanPrice()}</span></p>
                      )}
                      
                      {subscriptionData.activatedAt && (
                        <p>📅 Активирована: {new Date(subscriptionData.activatedAt).toLocaleDateString('ru-RU')}</p>
                      )}
                      
                      {subscriptionData.currentPeriodEnd && (
                        <p>⏳ Действует до: <span className="font-medium text-purple-700">{formatSubscriptionEndDate()}</span></p>
                      )}
                      
                      {subscriptionData.cancelAtPeriodEnd && (
                        <p className="text-orange-600 mt-2">
                          ⚠️ Подписка будет отменена после окончания текущего периода
                        </p>
                      )}
                      
                      <p className="text-xs text-gray-500 mt-2">
                        ID: {subscriptionData.stripeSubscriptionId?.slice(-8)}
                      </p>
                    </div>
                  ) : (
                    <div className="text-sm text-gray-600">
                      <p>🔓 Для доступа к PRO контенту оформите подписку</p>
                      <p className="text-xs text-gray-500 mt-2">
                        • Все медитативные практики рисования<br />
                        • Мини-пути и уроки<br />
                        • Сообщество единомышленников<br />
                        • Без дедлайнов и гонки
                      </p>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Кнопки действий */}
            <div className="p-6 pt-0 space-y-3 flex-shrink-0">
              {isAdmin && (
                <Link
                  to="/dashboardAdx"
                  onClick={() => setShowUserInfoPopup(false)}
                  className="w-full bg-gradient-to-r from-red-500 to-orange-500 text-white font-bold py-3 rounded-xl hover:opacity-90 transition-opacity flex items-center justify-center gap-2"
                >
                  <Shield className="w-5 h-5" />
                  Перейти в админ-панель
                </Link>
              )}

              {!premium ? (
                <button 
                  onClick={() => {
                    setShowUserInfoPopup(false);
                    setTimeout(() => setShowSubscriptionPopup(true), 100);
                  }}
                  className="w-full bg-gradient-to-r from-[#6C6FF8] to-[#F466C8] text-white font-bold py-3 rounded-xl hover:opacity-90 transition-opacity"
                >
                  Активировать PRO доступ
                </button>
              ) : (
                <button 
                  onClick={() => {
                    setShowUserInfoPopup(false);
                    setTimeout(() => setShowSubscriptionPopup(true), 100);
                  }}
                  className="w-full bg-gradient-to-r from-purple-600 to-pink-600 text-white font-bold py-3 rounded-xl hover:opacity-90 transition-opacity"
                >
                  Управление подпиской
                </button>
              )}
          
              
              {/* Кнопка выхода для веб-пользователей */}
              {isWebUser && (
                <button 
                  onClick={handleLogout}
                  className="w-full border border-red-200 text-red-600 font-medium py-3 rounded-xl hover:bg-red-50 transition-colors flex items-center justify-center gap-2"
                >
                  <LogOut className="w-4 h-4" />
                  Выйти из аккаунта
                </button>
              )}
              
              <button 
                onClick={() => setShowUserInfoPopup(false)}
                className="w-full border border-gray-300 text-gray-700 font-medium py-3 rounded-xl hover:bg-gray-50 transition-colors"
              >
                Закрыть
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Попап с подпиской */}
      <SubscriptionPopup
        isOpen={showSubscriptionPopup}
        onClose={() => setShowSubscriptionPopup(false)}
        subscriptionInfo={subscriptionInfo}
        loading={loading && showSubscriptionPopup}
      />
    </>
  );
};

export default Header;